import {Node, mergeAttributes} from '@tiptap/core'

export type ImageWrap = 'inline' | 'left' | 'right'

export interface ResizableImageOptions {
  inline: boolean
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      setImage: (options: {
        src: string
        alt?: string
        title?: string
        width?: number | string
        height?: number | string
        wrap?: ImageWrap
      }) => ReturnType
      setImageWrap: (wrap: ImageWrap) => ReturnType
    }
  }
}

function parseWrapFromElement(element: HTMLElement): ImageWrap {
  const dataWrap = element.getAttribute('data-wrap')
  if (dataWrap === 'left' || dataWrap === 'right') {
    return dataWrap
  }
  // Backwards compat with earlier wrap names
  if (dataWrap === 'square' || dataWrap === 'square-left') return 'left'
  if (dataWrap === 'tight' || dataWrap === 'tight-left') return 'left'
  if (dataWrap === 'square-right' || dataWrap === 'tight-right') return 'right'
  // Fall back to inferring from inline styles
  if (element.style.float === 'left') return 'left'
  if (element.style.float === 'right') return 'right'
  return 'inline'
}

function wrapStyles(wrap: ImageWrap): Record<string, string> {
  if (wrap === 'left') {
    return {
      'data-wrap': 'left',
      style: 'float: left; margin: 0.75em 1em 0.75em 0;',
    }
  }
  if (wrap === 'right') {
    return {
      'data-wrap': 'right',
      style: 'float: right; margin: 0.75em 0 0.75em 1em;',
    }
  }
  return {}
}

const ResizableImage = Node.create<ResizableImageOptions>({
  name: 'image',

  addOptions() {
    return {
      inline: true,
      HTMLAttributes: {},
    }
  },

  inline() {
    return this.options.inline
  },

  group() {
    return this.options.inline ? 'inline' : 'block'
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const width =
            element.getAttribute('width') ||
            element.style.width?.replace('px', '')
          return width ? Number(width) || width : null
        },
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {}
          }
          return {width: attributes.width}
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height =
            element.getAttribute('height') ||
            element.style.height?.replace('px', '')
          return height ? Number(height) || height : null
        },
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {}
          }
          return {height: attributes.height}
        },
      },
      wrap: {
        default: 'inline' as ImageWrap,
        parseHTML: (element) => parseWrapFromElement(element),
        renderHTML: (attributes) => wrapStyles(attributes.wrap),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ]
  },

  renderHTML({HTMLAttributes}) {
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
  },

  addCommands() {
    return {
      setImage: (options) => ({commands}) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        })
      },
      setImageWrap: (wrap) => ({tr, state, dispatch}) => {
        const {selection} = state
        const node = state.doc.nodeAt(selection.from)
        if (!node || node.type.name !== 'image') {
          return false
        }
        if (dispatch) {
          tr.setNodeMarkup(selection.from, undefined, {
            ...node.attrs,
            wrap,
          })
          dispatch(tr)
        }
        return true
      },
    }
  },

  addNodeView() {
    return ({node, getPos, editor}) => {
      const wrapper = document.createElement('span')
      wrapper.classList.add('image-resizable')

      const applyWrap = (el: HTMLElement, wrap: ImageWrap) => {
        el.classList.remove(
          'image-resizable--wrap-left',
          'image-resizable--wrap-right',
        )
        if (wrap && wrap !== 'inline') {
          el.classList.add(`image-resizable--wrap-${wrap}`)
        }
      }

      applyWrap(wrapper, node.attrs.wrap)

      const img = document.createElement('img')
      img.src = node.attrs.src
      if (node.attrs.alt) img.alt = node.attrs.alt
      if (node.attrs.title) img.title = node.attrs.title
      if (node.attrs.width) {
        img.style.width = `${node.attrs.width}px`
      }
      if (node.attrs.height) {
        img.style.height = `${node.attrs.height}px`
      }

      wrapper.appendChild(img)

      // Create resize handles
      const handlePositions = ['se', 'sw', 'ne', 'nw'] as const
      const handles: HTMLElement[] = []

      handlePositions.forEach((pos) => {
        const handle = document.createElement('span')
        handle.classList.add(
          'image-resize-handle',
          `image-resize-handle--${pos}`,
        )
        handle.dataset.direction = pos
        wrapper.appendChild(handle)
        handles.push(handle)
      })

      // Show handles on selection
      const showHandles = () =>
        wrapper.classList.add('image-resizable--selected')
      const hideHandles = () =>
        wrapper.classList.remove('image-resizable--selected')

      // Select node on click
      wrapper.addEventListener('click', () => {
        if (typeof getPos === 'function') {
          const pos = getPos()
          if (pos != null) {
            editor.commands.setNodeSelection(pos)
          }
        }
      })

      // Resize logic
      const onMouseDown = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        if (!target.classList.contains('image-resize-handle')) {
          return
        }

        event.preventDefault()
        event.stopPropagation()

        const startX = event.clientX
        const startWidth = img.offsetWidth || parseInt(img.style.width) || 200
        const startHeight =
          img.offsetHeight || parseInt(img.style.height) || 200
        const aspectRatio = startWidth / startHeight
        const direction = target.dataset.direction

        const onMouseMove = (e: MouseEvent) => {
          const deltaX = e.clientX - startX
          let newWidth: number

          if (direction === 'se' || direction === 'ne') {
            newWidth = Math.max(50, startWidth + deltaX)
          } else {
            newWidth = Math.max(50, startWidth - deltaX)
          }

          const newHeight = Math.round(newWidth / aspectRatio)
          img.style.width = `${Math.round(newWidth)}px`
          img.style.height = `${newHeight}px`
        }

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)

          const width = Math.round(
            parseFloat(img.style.width) || img.offsetWidth,
          )
          const height = Math.round(
            parseFloat(img.style.height) || img.offsetHeight,
          )

          if (typeof getPos === 'function') {
            const pos = getPos()
            if (pos != null) {
              editor.view.dispatch(
                editor.view.state.tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  width,
                  height,
                }),
              )
            }
          }
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      }

      wrapper.addEventListener('mousedown', onMouseDown)

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'image') {
            return false
          }

          img.src = updatedNode.attrs.src
          if (updatedNode.attrs.alt) img.alt = updatedNode.attrs.alt
          if (updatedNode.attrs.title) img.title = updatedNode.attrs.title
          if (updatedNode.attrs.width) {
            img.style.width = `${updatedNode.attrs.width}px`
          } else {
            img.style.width = ''
          }
          if (updatedNode.attrs.height) {
            img.style.height = `${updatedNode.attrs.height}px`
          } else {
            img.style.height = ''
          }

          applyWrap(wrapper, updatedNode.attrs.wrap)

          return true
        },
        selectNode: showHandles,
        deselectNode: hideHandles,
        destroy: () => {
          wrapper.removeEventListener('mousedown', onMouseDown)
        },
      }
    }
  },
})

export default ResizableImage
